import {Suspense, useEffect, useMemo, useRef} from 'react'
import {ErrorBoundary} from '@github-ui/react-core/error-boundary'
import type {Thread} from '@github-ui/diff-lines/conversation-types'
import {debounce} from '@github/mini-throttle'
import {announce} from '@github-ui/aria-live'
import {ThreadPreview} from './ThreadPreview'
import {CommentsSkeleton} from './CommentsSkeleton'
import {CommentsErrorFallback} from './CommentsErrorFallback'
import {HydrateThreadPositioning} from './HydrateThreadPositioning'

export interface ThreadPreviewsContentProps {
  onNavigateToDiffComment?: () => void
  threads: Thread[]
}

/**
 * Container that renders the thread list.
 * It also hydrates thread positioning for comments that fall outside the current diff.
 */
export function ThreadPreviewsContent({onNavigateToDiffComment, threads}: ThreadPreviewsContentProps) {
  return (
    <>
      <HydrateThreadPositioning threads={threads} />
      <ThreadPreviewsComponent threads={threads} onNavigateToDiffComment={onNavigateToDiffComment} />
    </>
  )
}

/**
 * Presentational thread previews renderer. It announces result counts, shows zero state
 * for empty filtered results.
 */
function ThreadPreviewsComponent({threads, onNavigateToDiffComment}: ThreadPreviewsContentProps) {
  const announceRef = useRef<HTMLDivElement>(null)

  const threadListRef = useRef<HTMLDivElement>(null)
  const debouncedAnnounce = useMemo(() => debounce(announce, 300), [])

  useEffect(() => {
    const element = announceRef.current as HTMLElement

    const announceText =
      threads.length > 0 ? `${threads.length} ${threads.length === 1 ? 'comment' : 'comments'}` : `No comments found`
    debouncedAnnounce(announceText, {element})
  }, [debouncedAnnounce, threads])

  return (
    <>
      {threads.length > 0 ? (
        <ErrorBoundary boundaryName="DockedThreadPreviewsContent" fallback={<CommentsErrorFallback />}>
          <Suspense fallback={<CommentsSkeleton reviewCommentsMode />}>
            <div ref={threadListRef} className="d-flex flex-column position-relative width-full gap-3">
              {threads.map(thread => (
                <ThreadPreview
                  key={thread.id}
                  thread={thread}
                  selectedReviewId={null}
                  onNavigateToDiffComment={onNavigateToDiffComment}
                  headingLevel="h2"
                />
              ))}
            </div>
          </Suspense>
        </ErrorBoundary>
      ) : null}
      <div className="sr-only" aria-live="polite" aria-atomic="true" ref={announceRef} />
    </>
  )
}
